import { useRef } from 'react';
import { Auth } from 'aws-amplify';
import cx from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { AppTracker, BottomPanelKey, StreamingPlatform as StreamingPlatformType } from 'types';
import { choosePaletteColor, displayRemainingPlanTime, getChannelURL, getUserInitials } from 'utils';
import { streamingPlatformsActions } from 'store/actions';
import {
  authSelector,
  availableMinutesSelector,
  currentPlanSelector,
  linkedStreamingPlatformsSelector,
  notLinkedStreamingPlatformsSelector,
  profileChannelNameSelector
} from 'store/selectors';
import { LoggerService, tracker } from 'services';
import { useBottomSheet, useConfirmation, useStore, useStreamingAccounts, useToast } from 'hooks';
import { AddStreamingPlatformsPanel, BottomPanels, ConfirmPanel, Navbar, StreamingPlatform } from 'containers';
import { Avatar } from 'components';
import { CopyIcon, LogoutIcon, PlusIcon } from 'components/Icon';

import styles from './ProfilePanel.module.scss';

export type ProfilePanelProps = {
  onUpgrade?(): void;
  onLoggedOut?(): void;
};

const ProfilePanel = ({ onUpgrade, onLoggedOut }: ProfilePanelProps) => {
  const dispatch = useDispatch();
  const { auth, channelName, currentPlan, availableMinutes, linkedPlatforms, notLinkedPlatforms } = useStore(state => ({
    auth: authSelector(state),
    channelName: profileChannelNameSelector(state),
    currentPlan: currentPlanSelector(state),
    availableMinutes: availableMinutesSelector(state),
    linkedPlatforms: linkedStreamingPlatformsSelector(state),
    notLinkedPlatforms: notLinkedStreamingPlatformsSelector(state)
  }));

  const toast = useToast();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const confirmation = useConfirmation<'LOGOUT'>();
  const bottomSheet = useBottomSheet<BottomPanelKey.ADD_STREAMING_ACCOUNT>();
  const streamingAccounts = useStreamingAccounts({
    onLinked: () => bottomSheet.close()
  });

  const handleOnCopied = () => toast.info('Copied!', { id: 'COPY_TO_CLIPBOARD' });

  const handleOnOpenAccountsPanel = () => bottomSheet.open(BottomPanelKey.ADD_STREAMING_ACCOUNT);

  const toggleAccount = (platform: StreamingPlatformType, checked: boolean) => {
    if (checked) {
      streamingAccounts.enable(platform);
    } else {
      streamingAccounts.disable(platform);
    }
  };

  const selectAccount = (platform: StreamingPlatformType) => {
    tracker.linkStreamingPlatform(platform, AppTracker.LinkStreamingPlatformSource.PROFILE_PANEL);
    streamingAccounts.link(platform);
  };

  const channelUrl = getChannelURL(channelName, true);

  const handleOnUpgrade = () => {
    tracker.upgradePlan(AppTracker.UpgradePlanSource.PROFILE_PANEL);

    onUpgrade?.();
  };

  const handleOnLogOut = () => {
    confirmation.open('LOGOUT');
  };

  const handleOnLogoutCancelled = () => {
    confirmation.close();
  };

  const handleOnLogoutSubmitted = async () => {
    try {
      tracker.signOut();
      dispatch(streamingPlatformsActions.reset());
      await Auth.signOut();
      onLoggedOut?.();
      confirmation.close();
    } catch (e) {
      LoggerService.log('LOGOUT', e);
      if (e instanceof Error) {
        toast.error(e.message, { id: 'LOGOUT' });
      }
    }
  };

  const initials = getUserInitials(auth.name);
  const color = choosePaletteColor(auth.name);

  return (
    <>
      <Navbar title="Profile" />

      <div ref={wrapperRef} className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.main}>
            <Avatar src={auth.image} className={styles.avatar} initials={initials} fallbackColor={color} />
            <div className={styles.info}>
              <h3 className={styles.title}>{auth.name}</h3>
              {auth.email && <h4 className={styles.subtitle}>{auth.email}</h4>}
              {auth.phoneNumber && <p className={styles.subtitle}>{auth.phoneNumber}</p>}
            </div>
          </div>

          <ul className={styles.extra}>
            {channelName && (
              <li className={cx(styles.item, styles.channel)}>
                <div className={styles.row}>
                  <div className={styles.content}>
                    <div className={styles.title}>
                      <span className={styles.label}>Channel:</span>
                      <Link to={getChannelURL(channelName)} className={styles.value}>
                        @{channelName}
                      </Link>
                    </div>
                  </div>

                  <CopyToClipboard text={channelUrl}>
                    <button type="button" className={styles.action} onClick={handleOnCopied}>
                      <CopyIcon height={20} width={20} />
                    </button>
                  </CopyToClipboard>
                </div>
              </li>
            )}

            <li className={cx(styles.item, styles.accounts)}>
              <div className={styles.row}>
                <div className={styles.content}>
                  <div className={styles.title}>
                    <span className={styles.label}>Linked accounts:</span>{' '}
                    <span className={styles.value}>{linkedPlatforms.length}</span>
                  </div>
                </div>
                {notLinkedPlatforms.length > 0 && (
                  <button type="button" className={styles.action} onClick={handleOnOpenAccountsPanel}>
                    <PlusIcon height={20} width={20} />
                  </button>
                )}
              </div>

              {linkedPlatforms.length > 0 && (
                <ul className={styles.list}>
                  {linkedPlatforms.map(platform => (
                    <li key={platform.key}>
                      <StreamingPlatform action="toggle" platform={platform.key} onChange={toggleAccount} />
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {currentPlan && (
              <li className={cx(styles.item, styles.plan)}>
                <div className={styles.row}>
                  <div className={styles.content}>
                    <h4 className={styles.title}>
                      <span className={styles.label}>Your plan:</span>{' '}
                      <span className={styles.value}>{currentPlan?.name}</span>
                    </h4>
                    <p className={cx(styles.subtitle, styles.hours)}>
                      You have left {displayRemainingPlanTime(availableMinutes)}
                    </p>
                  </div>
                  <button type="button" className={cx(styles.action, styles.upgrade)} onClick={handleOnUpgrade}>
                    Upgrade
                  </button>
                </div>
              </li>
            )}

            <li className={cx(styles.item, styles.logout)}>
              <div className={styles.row}>
                <button type="button" className={styles.action} onClick={handleOnLogOut}>
                  <LogoutIcon />
                  <span>Log out</span>
                </button>
              </div>
            </li>
          </ul>
        </div>

        <BottomPanels
          panels={[
            {
              name: BottomPanelKey.ADD_STREAMING_ACCOUNT,
              show: bottomSheet.state.contentKey === BottomPanelKey.ADD_STREAMING_ACCOUNT,
              title: 'Add streaming account',
              component: (
                <div className="container text-center py-4 pb-24">
                  <AddStreamingPlatformsPanel onClick={selectAccount} />
                </div>
              )
            }
          ]}
          onClose={bottomSheet.close}
        />

        <ConfirmPanel
          show={confirmation.state.isOpen}
          title="Do you want to log out?"
          name="LOGOUT"
          message="You will lose all your linked accounts"
          submitText="Log out"
          onCancel={handleOnLogoutCancelled}
          onSubmit={handleOnLogoutSubmitted}
        />
      </div>
    </>
  );
};

export default ProfilePanel;
